import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Camera, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const CameraCapture = ({ onCapture, onCancel, title = "Capture Photo" }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [error, setError] = useState(null);
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const initCamera = async () => {
      try {
        if (streamRef.current) {
          stopCamera();
        }

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        
        if (!isMounted) {
          mediaStream.getTracks().forEach(track => track.stop());
          return;
        }

        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.onloadedmetadata = () => {
            if (isMounted) setCameraReady(true);
          };
        }
        
        streamRef.current = mediaStream;
        setStream(mediaStream);
        setError(null);
      } catch (err) {
        console.error('Error accessing camera:', err);
        if (isMounted) {
          setError('Unable to access camera. Please ensure camera permissions are granted.');
        }
      }
    };

    initCamera();

    return () => {
      isMounted = false;
      stopCamera();
    };
  }, []);

  // Re-attach stream when retaking photo (video element remounts)
  useEffect(() => {
    if (!capturedImage && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [capturedImage]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setStream(null);
    }
  };

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setCapturedImage(base64String);
      };
      reader.readAsDataURL(blob);
    }, 'image/jpeg', 0.95);
  }, []);

  const retake = () => {
    setCapturedImage(null);
  };

  const confirm = () => {
    if (capturedImage && onCapture) {
      onCapture(capturedImage);
    }
  };

  const handleCancel = () => {
    stopCamera();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in">
      <Card className="max-w-2xl w-full p-6 space-y-4 animate-scale-in shadow-2xl border-0">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-semibold">{title}</h3>
          <Button variant="ghost" size="icon" onClick={handleCancel} className="hover:bg-muted rounded-full">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            {error}
          </div>
        )}

        <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
          {!capturedImage ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />
              
              {!cameraReady && !error && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="text-white text-center">
                    <Camera className="h-12 w-12 mx-auto mb-2 animate-pulse" />
                    <p>Initializing camera...</p>
                  </div>
                </div>
              )}

              <div className="absolute inset-0 border-4 border-primary/30 rounded-lg pointer-events-none">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-80 border-2 border-primary rounded-full" />
              </div>
            </>
          ) : (
            <img
              src={capturedImage}
              alt="Captured"
              className="w-full h-full object-cover"
            />
          )}
        </div>

        <div className="text-sm text-muted-foreground text-center">
          {!capturedImage ? (
            <p>Position your face within the oval guide and ensure good lighting</p>
          ) : (
            <p>Review your photo before confirming</p>
          )}
        </div>

        <div className="flex gap-3 justify-center">
          {!capturedImage ? (
            <Button
              onClick={capturePhoto}
              size="lg"
              disabled={!cameraReady || error}
              data-testid="capture-button"
              className="shadow-lg hover:shadow-xl active:scale-95 transition-all"
            >
              <Camera className="h-5 w-5 mr-2" />
              Capture Photo
            </Button>
          ) : (
            <>
              <Button onClick={retake} variant="outline" size="lg" data-testid="retake-button" className="hover:bg-muted/50 active:scale-95 transition-all">
                Retake
              </Button>
              <Button onClick={confirm} size="lg" data-testid="confirm-button" className="shadow-lg hover:shadow-xl active:scale-95 transition-all bg-primary hover:bg-primary/90">
                <Check className="h-5 w-5 mr-2" />
                Confirm
              </Button>
            </>
          )}
        </div>

        <p className="text-xs text-center text-muted-foreground">
          ⚠️ Live camera capture only - gallery uploads are not allowed for security
        </p>
      </Card>
    </div>
  );
};

export default CameraCapture;
